import { Loader2, Gift, MessageCircle } from "lucide-react";
import { Button } from "@/components/flowly/infra/Button/Button";
import { useLanguage } from "@/contexts/LanguageContext";
import { WHATSAPP_NUMBER } from "@/config/constants";
import type { CancellationReason } from "./types";

interface RetentionOfferStepProps {
  reason: CancellationReason;
  hasExistingDiscount: boolean;
  isLoading: boolean;
  onAcceptDiscount: () => void;
  onDeclineOffer: () => void;
  onKeepSubscription: () => void;
}

const WHATSAPP_MESSAGE_KEYS: Partial<Record<CancellationReason, string>> = {
  too_expensive: "whatsappMsgTooExpensive",
  not_using_enough: "whatsappMsgNotUsing",
  missing_features: "whatsappMsgFeatures",
  switching_competitor: "whatsappMsgSwitching",
  quality_not_good: "whatsappMsgQuality",
  too_complicated: "whatsappMsgComplicated",
};

export const RetentionOfferStep = ({
  reason,
  hasExistingDiscount,
  isLoading,
  onAcceptDiscount,
  onDeclineOffer,
  onKeepSubscription,
}: RetentionOfferStepProps) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-4">
      {/* Discount offer (hidden if user already has an active discount) */}
      {!hasExistingDiscount && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground">
              {t.cancellation.offerDiscount}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {t.cancellation.offerDiscountDesc}
          </p>
          <Button
            variant="primary"
            className="w-full"
            onClick={onAcceptDiscount}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t.cancellation.acceptOffer
            )}
          </Button>
        </div>
      )}

      {/* WhatsApp CTA */}
      <div className="rounded-xl border border-[#25D366]/30 bg-[#25D366]/10 p-4 space-y-3">
        <p className="text-sm text-foreground">
          {t.cancellation.whatsappCta}
        </p>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
            t.cancellation[WHATSAPP_MESSAGE_KEYS[reason] as keyof typeof t.cancellation] ?? ""
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1fba59]"
        >
          <MessageCircle className="w-4 h-4" />
          {t.cancellation.whatsappButton}
        </a>
      </div>

      {/* Keep subscription (primary) + Cancel anyway (de-emphasized) */}
      <div className="flex flex-col gap-2">
        <Button
          variant="accent"
          rounded="2xl"
          className="w-full"
          onClick={onKeepSubscription}
          disabled={isLoading}
        >
          {t.cancellation.keepSubscription}
        </Button>
        <Button
          variant="secondary"
          rounded="2xl"
          className="w-full"
          onClick={onDeclineOffer}
          disabled={isLoading}
        >
          {t.cancellation.cancelAnyway}
        </Button>
      </div>
    </div>
  );
};
